import { Button, Card, Divider, Tooltip } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { PageContainer } from '../../../../components/molecules/page-container';
import { PageHeader } from '../../../../components/molecules/page-header';
import { Plus } from '../../../../icons/plus';
import { useRemoveDestinationGroupFromDefault } from '../../hooks/mutation/use-remove-destination-group-from-default';
import { useSetDestinationGroupAsDefault } from '../../hooks/mutation/use-set-destination-group-as-default';
import { useDestinationGroups } from '../../hooks/query/use-destination-groups';
import {
  DestinationGroupsActionSlotPayload,
  DestinationGroupsTable,
} from '../organisms/destination-groups-table';
import { CreateDestinationGroupDialog } from '../organisms/dialogs/create-destination-group-dialog/create-destination-group-dialog';

import { ActionsTableMenu } from '@/components/atoms/actions-table-menu';
import { Pagination } from '@/components/organisms/pagination';
import { useDialog } from '@/hooks/use-dialog';
import { usePagination } from '@/hooks/use-pagination';
import { Can, usePermissions, withPermissions } from '@/permissions/can';

const DestinationGroups = () => {
  const { t } = useTranslation(['destinationGroups', 'common']);

  const permissions = usePermissions();

  const { changePage, changePerPage, page, perPage, totalPages, setTotalItems } = usePagination();

  const { data, isLoading, isFetching, error } = useDestinationGroups({ page, perPage });

  const [isOpenCreateGroupDialog, handleOpenCreateGroupDialog, handleCloseCreateGroupDialog] =
    useDialog();

  const { mutate: setGroupAsDefault } = useSetDestinationGroupAsDefault();

  const { mutate: removeGroupFromDefault } = useRemoveDestinationGroupFromDefault();

  const handleManageDefaultDestinationGroup = (
    id: number,
    isGroupDefaultForOrganization: boolean,
  ) => {
    if (isGroupDefaultForOrganization) {
      removeGroupFromDefault();
    } else {
      setGroupAsDefault(id);
    }
  };

  const actionSlot = useCallback(
    ({ id, isDefaultForOrganization }: DestinationGroupsActionSlotPayload) => {
      const actions = [
        {
          key: 'manage-default-group',
          label: isDefaultForOrganization
            ? t('destinationGroups:table.removeFromDefault')
            : t('destinationGroups:table.setAsDefault'),
          onClick: () => {
            handleManageDefaultDestinationGroup(id, isDefaultForOrganization);
          },
          disabled: permissions.cannot('manageDefaultGroup', 'DestinationsGroups'),
          tooltip: t('common:notSufficientPermissions'),
        },
      ];

      return <ActionsTableMenu actions={actions} />;
    },
    [],
  );

  useEffect(() => {
    if (data) {
      setTotalItems(data.total);
    }
  }, [data]);

  return (
    <PageContainer helmetPageTitle={t('destinationGroups:tab')}>
      <PageHeader
        title={t('destinationGroups:title')}
        extras={
          <Can I="create" a="DestinationsGroups" passThrough>
            {allowed => (
              <Tooltip
                title={t('common:notSufficientPermissions')}
                disableHoverListener={allowed}
                disableFocusListener={allowed}>
                <span>
                  <Button
                    color="primary"
                    onClick={handleOpenCreateGroupDialog}
                    variant="contained"
                    size="large"
                    disabled={!allowed}
                    startIcon={<Plus fontSize="small" />}>
                    {t('destinationGroups:create')}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Can>
        }
      />
      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        }}>
        <DestinationGroupsTable
          data={data?.resources || []}
          error={error?.response?.data?.message || ''}
          isLoading={isLoading}
          actionSlot={actionSlot}
        />
        <Divider sx={{ mt: 'auto' }} />
        <Pagination
          changePage={changePage}
          page={page}
          changePerPage={changePerPage}
          perPage={perPage}
          isFetching={isFetching}
          totalPages={totalPages}
        />
      </Card>
      <CreateDestinationGroupDialog
        open={isOpenCreateGroupDialog}
        onCancel={handleCloseCreateGroupDialog}
      />
    </PageContainer>
  );
};

export default withPermissions(
  permissions => permissions.cannot('read', 'DestinationsGroups'),
  DestinationGroups,
);
